import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Send, MapPin, Mail, Instagram, Twitter } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await axios.post(`${API}/api/contact`, formData);
      toast.success('Message sent successfully! I will get back to you soon.');
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="page-transition pt-20">
      {/* Header */}
      <section className="py-24 px-6 lg:px-12 max-w-7xl mx-auto">
        <div className="max-w-3xl">
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#525252] mb-4">
            Get in Touch
          </p>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl text-[#F5F5F0] tracking-tighter leading-none mb-8">
            Contact Us
          </h1>
          <p className="text-[#A3A3A3] text-lg leading-relaxed">
            Whether you're interested in commissioning a piece, inquiring about available 
            works, or simply want to connect—I'd love to hear from you.
          </p>
        </div>
      </section>

      {/* Contact Content */}
      <section className="pb-24 px-6 lg:px-12 max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
          {/* Contact Form */}
          <div className="lg:col-span-7">
            <form onSubmit={handleSubmit} className="space-y-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <label 
                    htmlFor="name" 
                    className="font-mono text-xs uppercase tracking-[0.2em] text-[#525252] block mb-3"
                  >
                    Name
                  </label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="bg-transparent border-0 border-b border-white/20 rounded-none px-0 py-3 text-[#F5F5F0] focus:border-[#C5A059] focus-visible:ring-0"
                    placeholder="Your name"
                    data-testid="contact-name"
                  />
                </div>
                <div>
                  <label 
                    htmlFor="email" 
                    className="font-mono text-xs uppercase tracking-[0.2em] text-[#525252] block mb-3"
                  >
                    Email
                  </label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="bg-transparent border-0 border-b border-white/20 rounded-none px-0 py-3 text-[#F5F5F0] focus:border-[#C5A059] focus-visible:ring-0"
                    placeholder="your@email.com"
                    data-testid="contact-email"
                  />
                </div>
              </div>

              <div>
                <label 
                  htmlFor="subject" 
                  className="font-mono text-xs uppercase tracking-[0.2em] text-[#525252] block mb-3"
                >
                  Subject
                </label>
                <Input
                  id="subject"
                  name="subject"
                  type="text"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                  className="bg-transparent border-0 border-b border-white/20 rounded-none px-0 py-3 text-[#F5F5F0] focus:border-[#C5A059] focus-visible:ring-0"
                  placeholder="What's this about?"
                  data-testid="contact-subject"
                />
              </div>

              <div>
                <label 
                  htmlFor="message" 
                  className="font-mono text-xs uppercase tracking-[0.2em] text-[#525252] block mb-3"
                >
                  Message
                </label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={6}
                  className="bg-transparent border-0 border-b border-white/20 rounded-none px-0 py-3 text-[#F5F5F0] focus:border-[#C5A059] focus-visible:ring-0 resize-none"
                  placeholder="Tell me about your inquiry..."
                  data-testid="contact-message"
                />
              </div>

              <Button
                type="submit"
                disabled={submitting}
                className="bg-[#C5A059] text-[#0A0A0A] hover:bg-[#E0BE77] font-mono text-xs uppercase tracking-[0.2em] px-8 py-6"
                data-testid="contact-submit"
              >
                {submitting ? (
                  'Sending...'
                ) : (
                  <>
                    <Send size={16} className="mr-3" />
                    Send Message
                  </>
                )}
              </Button>
            </form>
          </div>

          {/* Contact Info */}
          <div className="lg:col-span-4 lg:col-start-9">
            <div className="space-y-12">
              {/* Email */}
              <div>
                <div className="flex items-center gap-3 mb-3">
                  <Mail size={18} className="text-[#C5A059]" strokeWidth={1.5} />
                  <span className="font-mono text-xs uppercase tracking-[0.2em] text-[#525252]">
                    Email
                  </span>
                </div>
                <a 
                  href="mailto:gabodunrin@gmail.com"
                  className="text-[#F5F5F0] hover:text-[#C5A059] transition-colors"
                  data-testid="contact-info-email"
                >
                  gabodunrin@gmail.com
                </a>
              </div>

              {/* Location */}
              <div>
                <div className="flex items-center gap-3 mb-3">
                  <MapPin size={18} className="text-[#C5A059]" strokeWidth={1.5} />
                  <span className="font-mono text-xs uppercase tracking-[0.2em] text-[#525252]">
                    Studio
                  </span>
                </div>
                <p className="text-[#F5F5F0]">
                  Fayetteville, GA
                </p>
                <p className="text-[#A3A3A3] text-sm mt-1">
                  Studio visits by appointment only
                </p>
              </div>

              {/* Social */}
              <div>
                <span className="font-mono text-xs uppercase tracking-[0.2em] text-[#525252] block mb-4">
                  Follow
                </span>
                <div className="flex gap-4">
                  <a 
                    href="https://instagram.com/phaetonimagery" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-12 h-12 flex items-center justify-center border border-white/20 text-[#A3A3A3] hover:border-[#C5A059] hover:text-[#C5A059] transition-all"
                    data-testid="contact-instagram"
                  >
                    <Instagram size={20} strokeWidth={1.5} />
                  </a>
                  <a 
                    href="https://twitter.com/phaetonimagery" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-12 h-12 flex items-center justify-center border border-white/20 text-[#A3A3A3] hover:border-[#C5A059] hover:text-[#C5A059] transition-all"
                    data-testid="contact-twitter"
                  >
                    <Twitter size={20} strokeWidth={1.5} />
                  </a>
                </div>
              </div>

              {/* Note */}
              <div className="p-6 border border-white/10">
                <p className="text-[#A3A3A3] text-sm leading-relaxed">
                  Response time is typically 24-48 hours. For urgent inquiries 
                  regarding active auctions, please mention "AUCTION" in your subject line.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
